import { createContext, useEffect, useState } from 'react';
import { ThemeSwitcherProvider, useThemeSwitcher } from "react-css-theme-switcher";
import { IconsContext } from '@progress/kendo-react-common';
import { msalInstance } from '../index'
import { useFetchWithMsal } from '../hooks/useFetchWithMsal';
import { protectedResources } from '../authConfig';
import { AppUser, apiModelToAppUser } from '../models/appUser';
import SerialProvider from '../pages/serial/SerialProvider';

interface IAppUserContext {
    isStreckAdmin: boolean,
    isStreckQCAdmin: boolean,
    isReadOnly: boolean,
    adminView: boolean,
    appUser: AppUser | undefined,
    setAppUser: React.Dispatch<React.SetStateAction<AppUser | undefined>>,
    isLoading: boolean,
    refresh: Function
}
export interface IStreckContext {
    data: any[] | undefined,
    setData: React.Dispatch<React.SetStateAction<[] | undefined>>,
    refresh: Function,
}

{/* Split context into seperate providers to avoid re renders */ }
export const AppUserContext = createContext({} as IAppUserContext);
export const LabContext = createContext({} as IStreckContext);
export const PipetContext = createContext({} as IStreckContext);
export const OperatorContext = createContext({} as IStreckContext);

const useRefreshFunction = (endpoint: string, set: Function) => {
    const { execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    return () => {
        return execute("GET", endpoint)
            .then(
                ((result: any) => {
                    if (result) {
                        set(result);
                    }
                }
                ),
            )
    }
}

const AppUserProvider = (props: any) => {
    const [appUser, setAppUser] = useState(undefined as AppUser | undefined);
    const { switcher, themes } = useThemeSwitcher();

    const { execute, isLoading } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const getAppUser = () => {
        execute("GET", protectedResources.apiAppUser.endpoint + "GetUserByAzureId/?ObjectId=" + msalInstance.getActiveAccount()?.localAccountId)
            .then(
                ((result: any) => {
                    if (result) {
                        setAppUser(apiModelToAppUser(result));
                        if (result.darkMode) {
                            switcher({ theme: themes.dark });
                        }
                    }
                })
            );
    }

    useEffect(() => {
        getAppUser();
    }, [execute]);

    const activeAccount = msalInstance.getActiveAccount();
    const groups = activeAccount?.idTokenClaims?.groups as string[];
    const isStreckAdmin = (groups && groups.findIndex(x => x === 'PVSAdmin' || x === 'PVSAdminLabManager') > -1);
    const isStreckQCAdmin = (groups && groups.findIndex(x => x === 'PVSAdminLabManager') > -1);
    const isReadOnly = (groups && groups.findIndex(x => x === 'PVSAdminReadOnly') > -1);
    const adminView = (groups && groups.findIndex(x => x === 'PVSAdmin' || x === 'PVSAdminReadOnly' || x === 'PVSAdminLabManager') > -1);

    return <AppUserContext.Provider value={{ appUser: appUser, setAppUser: setAppUser, isLoading: isLoading, refresh: getAppUser, isStreckAdmin: isStreckAdmin, isStreckQCAdmin: isStreckQCAdmin, isReadOnly: isReadOnly, adminView: adminView }}>
        {props.children} {/* We use props.children here to avoid re renders */}
    </AppUserContext.Provider>
}

const LabProvider = (props: any) => {
    const [labs, setLabs] = useState(undefined as [] | undefined);
    const refresh = useRefreshFunction(protectedResources.apiLab.endpoint + 'GetAllLabs', setLabs);

    return <LabContext.Provider value={{ data: labs, setData: setLabs, refresh: refresh }}>
        {props.children} {/* We use props.children here to avoid re renders */}
    </LabContext.Provider>
}

const PipetProvider = (props: any) => {
    const [pipets, setPipets] = useState(undefined as [] | undefined);
    const refresh = useRefreshFunction(protectedResources.apiPipet.endpoint + 'GetActivePipets', setPipets);

    return <PipetContext.Provider value={{ data: pipets, setData: setPipets, refresh: refresh }}>
        {props.children} {/* We use props.children here to avoid re renders */}
    </PipetContext.Provider>
}

const OperatorProvider = (props: any) => {
    const [operators, setOperators] = useState(undefined as [] | undefined);
    const refresh = useRefreshFunction(protectedResources.apiOperator.endpoint + 'GetActiveOperators', setOperators);

    return <OperatorContext.Provider value={{ data: operators, setData: setOperators, refresh: refresh }}>
        {props.children} {/* We use props.children here to avoid re renders */}
    </OperatorContext.Provider>
}

const IconsProvider = (props: any) => {
    return <IconsContext.Provider value={{ type: 'svg' }}>
        {props.children} {/* We use props.children here to avoid re renders */}
    </IconsContext.Provider>
}

export const themes = {
    light: `${process.env.PUBLIC_URL}/styles/streck-light.css`,
    dark: `${process.env.PUBLIC_URL}/styles/streck-dark.css`,
};

export const PvsContextProvider = (props: any) => {
    return <ThemeSwitcherProvider defaultTheme="light" themeMap={themes}>
        <AppUserProvider>
            <LabProvider>
                <PipetProvider>
                    <OperatorProvider>
                        <IconsProvider>
                            <SerialProvider>
                                {props.children} {/* We use props.children here to avoid re renders */}
                            </SerialProvider>
                        </IconsProvider>
                    </OperatorProvider>
                </PipetProvider>
            </LabProvider>
        </AppUserProvider>
    </ThemeSwitcherProvider>
}